// This is the only allowed import of `atom`, to power the derived overloads of fileViewerAtom.
// eslint-disable-next-line no-restricted-imports
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { shallowEqual } from 'js/file_viewer/core/atoms/utils/shallowEqual';
// Jotai does not support storing pure functions directly in atoms, so to guarantee any type can be stored and
// accessed, we always wrap in a storable object.
export const fileViewerFunctionAtom = (initialValue) => {
    const wrappedAtom = fileViewerAtom({ val: initialValue });
    return fileViewerAtom((get) => {
        return get(wrappedAtom).val;
    }, (_get, set, newFunction) => {
        set(wrappedAtom, { val: newFunction });
    });
};
export function fileViewerAtom(initialValueOrRead, write) {
    if (!!write) {
        const aAtom = typeof initialValueOrRead === 'function'
            ? atom(initialValueOrRead, write)
            : atom(initialValueOrRead, write);
        return aAtom;
    }
    else {
        const aAtom = typeof initialValueOrRead === 'function'
            ? atom(initialValueOrRead)
            : atom(initialValueOrRead);
        return aAtom;
    }
}
/* eslint-enable no-redeclare */
// Inspired by https://github.com/pmndrs/jotai/discussions/2796.
const fileViewerAtomWithCompare = (readFvAtom, initialValue, areEqual) => {
    let previousValue = initialValue;
    const derivedAtom = fileViewerAtom((get) => {
        const next = readFvAtom(get);
        const arePreviousAndNextEqual = areEqual(previousValue, next);
        if (!arePreviousAndNextEqual) {
            previousValue = next;
            return next;
        }
        return previousValue;
    });
    return derivedAtom;
};
/**
 * A FileViewerAtom that will only re-render its consumers if its shallowEqual value changes. This is useful when you
 * want to combine and stabilize subsets of values from large, unstable atoms.
 */
export const shallowEqualFileViewerAtom = (readFvAtom, initialValue) => {
    return fileViewerAtomWithCompare(readFvAtom, initialValue, shallowEqual);
};
function fileViewerAtomFamily(initializeAtom, areEqual) {
    return atomFamily(initializeAtom, areEqual);
}
/**
 * @param defaultValue
 *    The default value for the atom. This value will be returned if the previewKey is undefined or not yet known, or
 *    if this atom is accessed before its value is set.
 * @param [preWriteSideEffects]
 *   If provided, we call this function before commiting a new value at the index `previewKey.serialized`. This
 *   function should return the new value to be set.
 * @returns A FileViewerAtom that stores a value for a given PreviewKey. This is useful for storing data that is
 *  specific to a particular file.
 * @note The internal atoms keyed by PreviewKey are retyped as FileViewerAtom<T | undefined> due to the
 *   possibility that previewKey is undefined or not yet known.
 * @note We can't provide a custom getter. Subscribers to a previewKeyAtom would not be updated if an atom referenced
 *   by a custom getter was updated. This can lead to subtle bugs where the atom is updated but the subscriber is not.
 */
export const previewKeyAtom = (defaultValue, preWriteSideEffects) => {
    const internalAtom = fileViewerAtom({});
    return fileViewerAtomFamily((previewKey) => fileViewerAtom((get) => {
        if (!previewKey) {
            return defaultValue;
        }
        const map = get(internalAtom);
        const val = map.hasOwnProperty(previewKey.serialized)
            ? map[previewKey.serialized]
            : defaultValue;
        return val;
    }, (get, set, ...args) => {
        if (!previewKey) {
            return;
        }
        const newVal = args[0];
        const getPreviewKeyAtom = (pkAtom) => {
            return get(pkAtom(previewKey));
        };
        const setPreviewKeyAtom = (pkAtom, ...args) => {
            set(pkAtom(previewKey), ...args);
        };
        const val = !!preWriteSideEffects
            ? preWriteSideEffects(get, set, getPreviewKeyAtom, setPreviewKeyAtom, ...args)
            : newVal;
        const prev = get(internalAtom);
        set(internalAtom, { ...prev, [previewKey.serialized]: val });
    }), (a, b) => (a === null || a === void 0 ? void 0 : a.serialized) === (b === null || b === void 0 ? void 0 : b.serialized) // areEqual
    );
};
//# sourceMappingURL=atom.preenvesnextjs.map
